import { useEffect, useState } from "react";
import { withRouter, Link } from "react-router-dom";
import AuthPageLayout from "../../layouts/VerticalLayouts/AuthPageLayout";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Common/Loader";

import {
  CardBody,
  Card,
  Input,
  Label,
  Form,
  FormFeedback,
  Button,
  Alert,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";
import ChangePWSuccessUI from "./ChangePWSuccessUI";
import {
  changePassword,
  resetChangePasswordStatus,
  resetChangePasswordError,
} from "../../store/actions";

const ChangePasswordPage = (props) => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", {
    page: t("auth.changePassword.pageTitle"),
  });

  const dispatch = useDispatch();

  // password registry validation
  const [invalidLength, setInvalidLength] = useState(true);
  const [missingNumber, setMissingNumber] = useState(true);
  const [missingLetter, setMissingLetter] = useState(true);

  const urlToken = props.match.params.token;
  const { status, error } = useSelector((state) => ({
    status: state.ChangePassword.status,
    error: state.ChangePassword.error,
  }));

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      token: urlToken,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },

    validationSchema: Yup.object({
      currentPassword: Yup.string().required(
        t("auth.changePassword.currentPasswordRequired")
      ),
      newPassword: Yup.string()
        .test("num", "Invalid password", (val) => {
          let numberRegex = new RegExp(".*[0-9].*");
          if (numberRegex.test(val)) {
            setMissingNumber(false);
          } else {
            setMissingNumber(true);
          }
          return numberRegex.test(val);
        })
        .test("letter", "Invalid password", (val) => {
          let letterRegex = new RegExp("[a-zA-Z]");
          if (letterRegex.test(val)) {
            setMissingLetter(false);
          } else {
            setMissingLetter(true);
          }
          return letterRegex.test(val);
        })
        .test("len", "Invalid password", (val) => {
          let lengthRegex = new RegExp("[0-9a-zA-Z]{8,}");
          if (lengthRegex.test(val)) {
            setInvalidLength(false);
          } else {
            setInvalidLength(true);
          }
          return lengthRegex.test(val);
        }),
      confirmPassword: Yup.string()
        .required(t("auth.validation.reenterNewPassword"))
        .when("newPassword", {
          is: (val) => (val && val.length > 0 ? true : false),
          then: Yup.string().oneOf(
            [Yup.ref("newPassword")],
            t("auth.validation.passwordMismatch")
          ),
        }),
    }),

    onSubmit: (values) => {
      dispatch(changePassword(values));
    },
  });

  // to-do refactor this
  const passwordPeek = () => {
    const pw = document.querySelector("[data-id='newPassword']");
    if (pw.type === "password") {
      pw.type = "text";
    } else {
      pw.type = "password";
    }
  };

  useEffect(() => {
    dispatch(resetChangePasswordStatus());
    dispatch(resetChangePasswordError());
  }, [dispatch]);

  return (
    <AuthPageLayout>
      <Card className="mt-4">
        <CardBody className="p-4">
          {status === "success" ? (
            <ChangePWSuccessUI />
          ) : (
            <>
              <div className="text-center mt-2 card__top-heading">
                <h5 className="text-primary">
                  {t("auth.changePassword.heading")}
                </h5>
                <p className="text-muted">
                  {t("auth.changePasswordSubheading")}
                </p>
              </div>
              <div className="p-2 mt-4">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                  className="needs-validation"
                  action="#"
                >
                  {error && (
                    <Alert color="danger" className="text-center fw-semibold">
                      {translateApiError(error)}
                    </Alert>
                  )}
                  <div className="mb-3">
                    <Label htmlFor="currentPassword" className="form-label">
                      {t("auth.changePassword.currentPassword")}
                    </Label>
                    <div className="position-relative auth-pass-inputgroup mb-3">
                      <Input
                        data-id="currentPassword"
                        id="currentPassword"
                        name="currentPassword"
                        className="form-control"
                        placeholder={t(
                          "auth.changePassword.currentPasswordPlaceholder"
                        )}
                        type="password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.currentPassword || ""}
                        invalid={
                          validation.touched.currentPassword &&
                          validation.errors.currentPassword
                            ? true
                            : false
                        }
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="newPassword" className="form-label">
                      {t("auth.fields.newPassword")}
                    </Label>
                    <div className="position-relative auth-pass-inputgroup mb-3">
                      <Input
                        data-id="newPassword"
                        id="newPassword"
                        name="newPassword"
                        className="form-control"
                        placeholder={t(
                          "auth.changePassword.newPasswordPlaceholder"
                        )}
                        type="password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.newPassword || ""}
                        invalid={
                          validation.touched.newPassword &&
                          validation.errors.newPassword
                            ? true
                            : false
                        }
                      />
                      {validation.touched.newPassword &&
                      validation.errors.newPassword &&
                      validation.values.newPassword ? (
                        <div className="custom-invalid-password">
                          <p>{t("auth.passwordRules.title")}</p>
                          <ul>
                            <li className={missingNumber ? "invalid" : ""}>
                              {t("auth.passwordRules.number")}
                            </li>
                            <li className={missingLetter ? "invalid" : ""}>
                              {t("auth.passwordRules.letter")}
                            </li>
                            <li className={invalidLength ? "invalid" : ""}>
                              {t("auth.passwordRules.length")}
                            </li>
                          </ul>
                        </div>
                      ) : null}
                      <button
                        className={
                          "btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted " +
                          (validation.errors.newPassword && "has-error")
                        }
                        type="button"
                        id="password-addon"
                        onClick={() => passwordPeek()}
                      >
                        <i className="ri-eye-fill align-middle"></i>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="confirmPassword" className="form-label">
                      {t("auth.changePassword.confirmPassword")}
                    </Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder={t(
                        "auth.changePassword.confirmPasswordPlaceholder"
                      )}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.confirmPassword || ""}
                      invalid={
                        validation.touched.confirmPassword &&
                        validation.errors.confirmPassword
                          ? true
                          : false
                      }
                    />
                    {validation.touched.confirmPassword &&
                    validation.errors.confirmPassword ? (
                      <FormFeedback type="invalid">
                        {validation.errors.confirmPassword}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mt-4">
                    <Button
                      color="warning"
                      type="submit"
                      block
                      className="text-primary"
                      onClick={validation.handleSubmit}
                      disabled={status === "loading" ? true : false}
                    >
                      {status === "loading" ? (
                        <Loader size="sm"></Loader>
                      ) : (
                        t("auth.changePassword.submit")
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </>
          )}
        </CardBody>
      </Card>
      {status !== "success" && (
        <div className="mt-4 text-center">
          <p className="mb-0">
            {t("auth.changePassword.cancelPrompt")}
            <Link
              to="/account"
              className="text-info text-decoration-underline ml-4"
            >
              {t("auth.actions.account")}
            </Link>
          </p>
        </div>
      )}
    </AuthPageLayout>
  );
};

export default withRouter(ChangePasswordPage);
